/**
 * Returns all focusable elements within a container.
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
const getFocusableElements = (container) =>
  Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details > summary',
    ),
  ).filter((el) => !el.closest('[inert]'));

/**
 * Opens a modal/drawer.
 * @param {HTMLElement|string} modal — the modal element or a CSS selector
 * @param {HTMLElement|null} triggerElement — element to return focus to on close
 */
const openModal = (modal, triggerElement = null) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  foundModal._a11yTrigger =
    triggerElement ?? (document.activeElement !== document.body ? document.activeElement : null);

  foundModal.style.display = 'block';
  document.querySelector('html').style.overflow = 'hidden';

  if (foundModal.id === 'MinicartDrawer') {
    document.querySelector('html').style.position = 'fixed';
    document.querySelector('html').style.width = '100%';
  }

  setTimeout(() => {
    window.requestAnimationFrame(() => {
      foundModal.classList.add('open');
      foundModal.style.display = '';

      // Second frame: the modal only became visible in the frame above, so focus
      // would be silently dropped if it were called before layout is flushed.
      window.requestAnimationFrame(() => {
        const focusable = getFocusableElements(foundModal);
        if (focusable.length > 0) focusable[0].focus();
        else if (foundModal.getAttribute('tabindex')) foundModal.focus();
      });

      foundModal._a11yEscHandler = (e) => {
        if (e.key === 'Escape') closeModal(foundModal);
      };
      document.addEventListener('keydown', foundModal._a11yEscHandler);

      // Focus trap is opt-in: only modals marked [data-focus-trap] cycle Tab within themselves.
      if (foundModal.hasAttribute('data-focus-trap')) {
        foundModal._a11yTrapHandler = (e) => {
          if (e.key !== 'Tab') return;

          const trappable = getFocusableElements(foundModal);
          if (!trappable.length) {
            e.preventDefault();
            return;
          }

          const first = trappable[0];
          const last = trappable[trappable.length - 1];

          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
        document.addEventListener('keydown', foundModal._a11yTrapHandler);
      }
    });
  }, 100);
};

const closeModal = (modal) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  if (foundModal._a11yEscHandler) {
    document.removeEventListener('keydown', foundModal._a11yEscHandler);
    foundModal._a11yEscHandler = null;
  }

  if (foundModal._a11yTrapHandler) {
    document.removeEventListener('keydown', foundModal._a11yTrapHandler);
    foundModal._a11yTrapHandler = null;
  }

  const transitionDuration = parseFloat(getComputedStyle(foundModal).transitionDuration.replace('s', '')) * 1000;
  foundModal.style.display = 'block';
  window.requestAnimationFrame(() => {
    foundModal.classList.remove('open');
    setTimeout(() => {
      foundModal.style.display = '';
      document.querySelector('html').style.overflow = '';

      if (foundModal.id === 'MinicartDrawer') {
        document.querySelector('html').style.position = '';
        document.querySelector('html').style.width = '';
      }

      foundModal._a11yTrigger?.focus();
      foundModal._a11yTrigger = null;
    }, transitionDuration);
  });
};

const initModals = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  document.querySelectorAll('.modal').forEach((modal) => {
    modal
      .querySelectorAll('.modal__backdrop , .modal__close, .modal__refuse')
      .forEach((closer) => closer.addEventListener('click', () => closeModal(modal)));
    // Escape is bound on `document` by openModal, so it no longer depends on the
    // modal carrying a tabindex — it now works on minicart, filters and localization too.
    if (modal.id)
      rootEl.querySelectorAll(`a[href*="${modal.id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openModal(modal, btn);
        }),
      );
  });
};

export { initModals, openModal, closeModal, getFocusableElements };
